import React, { useState, useEffect, useRef } from 'react';
import Icon from 'components/AppIcon';

const TwoFactorModal = ({ onSuccess, onClose }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(30);
  const inputRefs = useRef([]);

  const mockCode = '123456';

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleInputChange = (index, value) => {
    if (value.length > 1) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newCode.every(digit => digit !== '') && newCode.join('') === mockCode) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (verificationCode = code.join('')) => {
    if (verificationCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (verificationCode === mockCode) {
        onSuccess();
      } else {
        setError('Invalid verification code. Please try again.');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    setTimeLeft(30);
    setError('');
    alert('New verification code sent to your authenticator app');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-modal">
      <div className="bg-surface rounded-trading shadow-trading-modal max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-heading-semibold text-text-primary">
              Two-Factor Authentication
            </h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-secondary-50 rounded-trading transition-colors duration-trading-fast"
            >
              <Icon name="X" size={20} className="text-text-secondary" />
            </button>
          </div>

          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Shield" size={32} className="text-primary" />
            </div>
            <p className="text-text-secondary font-body-normal">
              Enter the 6-digit code from your authenticator app to complete sign in.
            </p>
          </div>

          {/* Code Input */}
          <div className="flex justify-center space-x-3 mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-12 text-center text-lg font-data border rounded-trading bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-trading-fast ${
                  error ? 'border-error' : 'border-border'
                }`}
                disabled={isLoading}
              />
            ))}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-error-50 border border-error-100 rounded-trading">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <p className="text-sm text-error font-body-normal">{error}</p>
              </div>
            </div>
          )}

          {/* Demo Code Info */}
          <div className="mb-6 p-3 bg-primary-50 border border-primary-100 rounded-trading">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5" />
              <div className="text-xs text-primary">
                <p className="font-body-medium mb-1">Demo 2FA Code:</p>
                <p className="font-data text-lg">{mockCode}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={() => handleVerify()}
              disabled={isLoading || code.some(digit => digit === '')}
              className="w-full bg-primary text-white px-4 py-3 rounded-trading font-body-medium text-sm hover:bg-primary-600 transition-colors duration-trading-fast disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Verifying...
                </>
              ) : (
                <>
                  <Icon name="CheckCircle" size={18} className="mr-2" />
                  Verify Code
                </>
              )}
            </button>

            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary font-body-normal">
                Didn't receive a code?
              </span>
              {timeLeft > 0 ? (
                <span className="text-text-secondary font-body-normal">
                  Resend in {timeLeft}s
                </span>
              ) : (
                <button
                  onClick={handleResendCode}
                  className="text-primary hover:text-primary-600 font-body-medium transition-colors duration-trading-fast"
                >
                  Resend Code
                </button>
              )}
            </div>
          </div>

          {/* Alternative Options */}
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-center text-sm text-text-secondary font-body-normal mb-3">
              Having trouble with your authenticator?
            </p>
            <button className="w-full text-primary hover:text-primary-600 font-body-medium text-sm transition-colors duration-trading-fast">
              Use backup recovery code instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorModal;